import { useState } from "react";
import { Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import SlotPicker from "@/components/SlotPicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";

const btn = "inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-semibold transition-colors";

export function CancelBookingButton({ booking, onDone }) {
  const cancel = async () => {
    try {
      await api.post(`/bookings/${booking.id}/cancel`);
      toast.success("Booking cancelled");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger data-testid={`cancel-booking-${booking.id}`} className={`${btn} border border-rose-200 text-rose-600 hover:bg-rose-50`}>Cancel</AlertDialogTrigger>
      <AlertDialogContent className="rounded-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel this booking?</AlertDialogTitle>
          <AlertDialogDescription>{booking.service_name} with {booking.provider_name}. This cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="cancel-dialog-keep">Keep booking</AlertDialogCancel>
          <AlertDialogAction data-testid="cancel-dialog-confirm" onClick={cancel} className="bg-rose-600 hover:bg-rose-700">Yes, cancel</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function RescheduleButton({ booking, onDone }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const pending = booking.reschedule_request?.status === "pending";
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/reschedule-request`, { date, start_time: time, note });
      toast.success("Reschedule request sent to the provider");
      setOpen(false);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button data-testid={`reschedule-booking-${booking.id}`} onClick={() => setOpen(true)} className={`${btn} border border-slate-300 text-slate-700 hover:border-sky-500`}>
        {pending ? "Change request" : "Reschedule"}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-2xl" data-testid="reschedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Request a new time</DialogTitle>
            <DialogDescription>The provider will review your request. Your current time stays reserved until they accept.</DialogDescription>
          </DialogHeader>
          <SlotPicker providerId={booking.provider_id} serviceId={booking.service_id} date={date} time={time} onDate={setDate} onTime={setTime} />
          <Textarea data-testid="reschedule-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note (optional)" rows={2} />
          <button data-testid="reschedule-submit" disabled={!date || !time || busy} onClick={submit} className={`${btn} h-12 bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-40`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send request"}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ReviewButton({ booking, onDone }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/review`, { rating, comment });
      toast.success("Thanks for your review!");
      setOpen(false);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button data-testid={`review-booking-${booking.id}`} onClick={() => setOpen(true)} className={`${btn} bg-amber-400 text-slate-900 hover:bg-amber-500`}>
        <Star className="mr-1 h-4 w-4" />Leave a review
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Review {booking.provider_name}</DialogTitle>
            <DialogDescription>{booking.service_name}</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center gap-1" data-testid="review-stars">
            {[1, 2, 3, 4, 5].map((i) => (
              <button key={i} type="button" data-testid={`review-star-${i}`} onClick={() => setRating(i)} aria-label={`${i} stars`}>
                <Star className={`h-9 w-9 transition-transform hover:scale-110 ${i <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
              </button>
            ))}
          </div>
          <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your experience (min 5 characters)" rows={4} />
          <button data-testid="review-submit" disabled={comment.trim().length < 5 || busy} onClick={submit} className={`${btn} h-12 bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-40`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit review"}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function CustomerActions({ booking, onDone }) {
  const active = ["pending", "confirmed"].includes(booking.status) && !booking.is_past;
  return (
    <div className="flex flex-wrap gap-2">
      {active && <RescheduleButton booking={booking} onDone={onDone} />}
      {active && <CancelBookingButton booking={booking} onDone={onDone} />}
      {booking.status === "completed" && !booking.reviewed && <ReviewButton booking={booking} onDone={onDone} />}
      {booking.status === "completed" && booking.reviewed && <span className="text-xs font-medium text-emerald-700" data-testid={`reviewed-${booking.id}`}>Reviewed</span>}
    </div>
  );
}
